import React, { useState, useEffect } from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { media } from "../theme/breakpoints";
import ActionBar from "../../common/ActionBar";
import { Padding } from "../../internal/EsignerTable";
import { navigate } from "@reach/router";
import _ from "lodash";
import BookingDetail from "../Bookings/BookingDetail";
import BookingService from "../../../services/BookingService";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";

const ROOMS = [
  {
    id: 1,
    code: "Room-1",
    name: "Hearing Room 1",
    capacity: 12,
    arbitrators: 3,
    area: 350,
  },
  {
    id: 2,
    code: "Room-2",
    name: "Hearing Room 2",
    capacity: 12,
    arbitrators: 3,
    area: 350,
  },
  {
    id: 3,
    code: "Room-3",
    name: "Hearing Room 3",
    capacity: 20,
    arbitrators: 3,
    area: 550,
  },
  {
    id: 4,
    code: "Library-Hall",
    name: "Library Hall",
    capacity: 10,
    arbitrators: 0,
    area: 280,
    isLibrary: true,
  },
];

const getRoomColor = (roomCode) => {
  const colorMap = {
    "Room-A": "#00838c",
    "Room-B": "#293461",
    "Room-C": "#F6B436",
    "Library-Hall": "#C98D18",
    "Room-1": "#00838c",
    "Room-2": "#293461",
    "Room-3": "#F6B436",
  };
  return colorMap[roomCode] || "#8E99A8";
};

const getStatusColor = (booking) => {
  if (
    booking.booking_status === "confirmed" &&
    booking.payment_status === "paid"
  ) {
    return COLORS.BTN_GREEN;
  }
  if (booking.booking_status === "reserved") return "#F6B436";
  return "#64748b";
};

const CalendarPage = () => {
  const [monthOffset, setMonthOffset] = useState(0);
  const [openBooking, setOpenBooking] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [expandedDate, setExpandedDate] = useState(null);

  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const handleOpenBooking = (b) => setOpenBooking(b);
  const handleCloseBooking = () => setOpenBooking(null);

  const onBreadcrumbClick = (selected) => {
    if (_.snakeCase(selected) === "booking_dashboard") {
      navigate("/dashboard/case-manager/booking-dashboard");
    }
  };

  // Calculate current view month correctly
  const now = new Date();
  const viewMonth = new Date(
    now.getFullYear(),
    now.getMonth() + monthOffset,
    1,
  );

  const currentMonth = viewMonth.getMonth() + 1; // 1-based for API
  const currentYear = viewMonth.getFullYear();

  const monthName = viewMonth.toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });

  // Fetch Bookings
  useEffect(() => {
    async function fetchBookings() {
      try {
        setLoader({ state: true, message: `Loading ${monthName}...` });
        const response = await BookingService.getBookingsDet({
          month: currentMonth,
          year: currentYear,
        });
        const bookingList = response?.data?.data || [];
        setBookings(bookingList);
        setExpandedDate(null);
      } catch (error) {
        enqueueSnackbar(error.message, { variant: "error" });
        setBookings([]);
      } finally {
        setLoader({ state: false });
      }
    }

    fetchBookings(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, currentYear, monthName]);

  // Build Calendar
  const firstDay = viewMonth.getDay();
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);

  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${currentYear}-${String(currentMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

    const dayBookings = bookings
      .filter((b) => formatDate(b.hearing_date) === dateStr)
      .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));

    days.push({
      date: dateStr,
      day: d,
      bookings: dayBookings,
      isToday: dateStr === todayStr,
      dayOfWeek: new Date(dateStr).getDay(),
    });
  }

  const isDateExpanded = (dateStr) => expandedDate === dateStr;

  return (
    <>
      <ActionBar
        breadcrumbs={["Booking Dashboard", "Calendar"]}
        {...{ onBreadcrumbClick }}
      />

      <Padding>
        <PageWrap>
          <Card
            $padding="0"
            style={{ overflow: "hidden", borderRadius: "16px" }}
          >
            <HeaderRow>
              <HeaderTitleBlock>
                <HeaderTitle>
                  <TitleIcon>
                    <i className="ti ti-calendar-month" aria-hidden="true" />
                  </TitleIcon>
                  {monthName}
                </HeaderTitle>
                <HeaderSub>
                  <i className="ti ti-info-circle" aria-hidden="true" />
                  Tap any booking to view details
                </HeaderSub>
              </HeaderTitleBlock>

              <NavButtons>
                <NavBtn
                  onClick={() => setMonthOffset(monthOffset - 1)}
                  title="Previous month"
                >
                  <i className="ti ti-chevron-left" aria-hidden="true" />
                  Previous
                </NavBtn>
                <NavBtn
                  $highlight={monthOffset === 0}
                  onClick={() => setMonthOffset(0)}
                  title="Current month"
                >
                  <i className="ti ti-circle-dot" aria-hidden="true" />
                  Today
                </NavBtn>
                <NavBtn
                  onClick={() => setMonthOffset(monthOffset + 1)}
                  title="Next month"
                >
                  Next
                  <i className="ti ti-chevron-right" aria-hidden="true" />
                </NavBtn>
              </NavButtons>
            </HeaderRow>

            <Legend>
              <LegendLabel>
                <i className="ti ti-door-2" aria-hidden="true" />
                ROOMS
              </LegendLabel>
              <LegendItems>
                {ROOMS.map((r) => (
                  <LegendItem key={r.id}>
                    <Swatch $color={getRoomColor(r.code)} />
                    <LegendItemText>{r.name}</LegendItemText>
                  </LegendItem>
                ))}
              </LegendItems>
            </Legend>

            <DayHeaderRow>
              {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d) => (
                <DayHeaderCell key={d}>{d}</DayHeaderCell>
              ))}
            </DayHeaderRow>

            <CalGrid>
              {days.map((d, i) => (
                <CellWrapper key={i} $empty={!d}>
                  {d && (
                    <Cell
                      $today={d.isToday}
                      $weekend={d.dayOfWeek === 0 || d.dayOfWeek === 6}
                      $hasBookings={d.bookings.length > 0}
                    >
                      <CellHeader>
                        <DayLabel
                          $today={d.isToday}
                          $weekend={d.dayOfWeek === 0 || d.dayOfWeek === 6}
                        >
                          {d.day}
                          {d.isToday && <TodayBadge>TODAY</TodayBadge>}
                        </DayLabel>
                      </CellHeader>

                      <BookingCountBadge $count={d.bookings.length}>
                        {d.bookings.length > 0 && (
                          <>
                            <span>{d.bookings.length}</span>
                          </>
                        )}
                      </BookingCountBadge>

                      <BookingsContainer $expanded={isDateExpanded(d.date)}>
                        {d.bookings.length === 0 ? (
                          <NoBookingState>
                            <NoBookingIcon>
                              <i
                                className="ti ti-calendar-off"
                                aria-hidden="true"
                              />
                            </NoBookingIcon>
                            <NoBookingText>No bookings</NoBookingText>
                          </NoBookingState>
                        ) : (
                          <>
                            {d.bookings
                              .slice(
                                0,
                                isDateExpanded(d.date) ? d.bookings.length : 2,
                              )
                              .map((b, bi) => (
                                <BookingChip
                                  key={bi}
                                  $color={getRoomColor(b.hearing_room)}
                                  onClick={() => handleOpenBooking(b)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      handleOpenBooking(b);
                                    }
                                  }}
                                >
                                  <ChipHeader>
                                    <ChipTime $color={getStatusColor(b)}>
                                      <i
                                        className="ti ti-clock"
                                        aria-hidden="true"
                                      />
                                      {b.start_time?.slice(0, 5)}
                                    </ChipTime>
                                    <ChipStatus $status={b.booking_status}>
                                      {b.booking_status === "confirmed" &&
                                      b.payment_status === "paid"
                                        ? "Paid"
                                        : b.booking_status === "reserved"
                                          ? "Reserved"
                                          : _.startCase(b.booking_status)}
                                    </ChipStatus>
                                  </ChipHeader>
                                  <ChipRoom>
                                    {/* <RoomIcon
                                      $color={getRoomColor(b.hearing_room)}
                                    >
                                      <i
                                        className="ti ti-door-2"
                                        aria-hidden="true"
                                      />
                                    </RoomIcon> */}
                                    {b.hearing_room.replace("Hearing ", "")}
                                  </ChipRoom>
                                  <ChipRef>{b.booking_reference}</ChipRef>
                                </BookingChip>
                              ))}

                            {d.bookings.length > 2 &&
                              !isDateExpanded(d.date) && (
                                <ExpandButton
                                  onClick={() => setExpandedDate(d.date)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      setExpandedDate(d.date);
                                    }
                                  }}
                                >
                                  <i
                                    className="ti ti-plus-circle"
                                    aria-hidden="true"
                                  />
                                  View {d.bookings.length - 2} more
                                </ExpandButton>
                              )}

                            {isDateExpanded(d.date) &&
                              d.bookings.length > 2 && (
                                <CollapseButton
                                  onClick={() => setExpandedDate(null)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      setExpandedDate(null);
                                    }
                                  }}
                                >
                                  <i
                                    className="ti ti-minus-circle"
                                    aria-hidden="true"
                                  />
                                  Show less
                                </CollapseButton>
                              )}
                          </>
                        )}
                      </BookingsContainer>
                    </Cell>
                  )}
                </CellWrapper>
              ))}
            </CalGrid>
          </Card>
        </PageWrap>
      </Padding>

      <BookingDetail booking={openBooking} onClose={handleCloseBooking} />
    </>
  );
};

export default CalendarPage;

const PageWrap = styled.div`
  padding: 24px 16px;
  max-width: 1520px;
  margin: 0 auto;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;

  ${media.tablet} {
    padding: 32px 28px;
  }

  ${media.laptop} {
    padding: 40px 48px;
  }
`;

const Card = styled.div`
  background: ${COLORS.PRIMARY_WHITE};
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: ${({ $padding }) =>
    $padding !== undefined ? `${$padding}px` : "22px"};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const HeaderRow = styled.div`
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);

  ${media.tablet} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
  }
`;

const HeaderTitleBlock = styled.div`
  flex: 1;
`;

const HeaderTitle = styled.h2`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -0.5px;
`;

const TitleIcon = styled.span`
  font-size: 28px;
  color: #00838c;
  opacity: 0.9;
`;

const HeaderSub = styled.p`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13.5px;
  color: #64748b;
  margin: 8px 0 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;

  i {
    font-size: 14px;
  }
`;

const NavButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  ${media.tablet} {
    flex-wrap: nowrap;
  }
`;

const NavBtn = styled.button`
  padding: 12px 20px;
  border-radius: 12px;
  border: 1.5px solid;
  border-color: ${({ $highlight }) => ($highlight ? "#10b981" : "#e2e8f0")};
  background: ${({ $highlight }) =>
    $highlight ? "rgba(16, 185, 129, 0.08)" : "#ffffff"};
  color: ${({ $highlight }) => ($highlight ? "#059669" : "#475569")};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  i {
    font-size: 15px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
    border-color: ${({ $highlight }) => ($highlight ? "#059669" : "#cbd5e1")};
    background: ${({ $highlight }) =>
      $highlight
        ? "rgba(16, 185, 129, 0.12)"
        : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)"};
  }

  &:active {
    transform: translateY(0);
  }
`;

const Legend = styled.div`
  padding: 18px 32px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  background: #f8fafc;
`;

const LegendLabel = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    font-size: 14px;
  }
`;

const LegendItems = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #475569;
  font-weight: 500;
`;

const LegendItemText = styled.span`
  transition: color 0.2s ease;
`;

const Swatch = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 6px;
  background: ${({ $color }) => $color};
  box-shadow: 0 2px 6px ${({ $color }) => `${$color}40`};
  flex-shrink: 0;
`;

const DayHeaderRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: linear-gradient(to right, #f1f5f9, #f8fafc);
  border-bottom: 1px solid #e2e8f0;
`;

const DayHeaderCell = styled.div`
  padding: 16px 8px;
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-align: center;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-right: 1px solid #e2e8f0;

  &:last-child {
    border-right: none;
  }
`;

const CalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
`;

const CellWrapper = styled.div`
  background: ${({ $empty }) => ($empty ? "#f8fafc" : "white")};
  border-right: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  aspect-ratio: auto;

  &:nth-child(7n) {
    border-right: none;
  }

  &:nth-last-child(-n + 7) {
    border-bottom: none;
  }
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 160px;
  padding: 14px;
  gap: 12px;
  background: ${({ $today, $weekend, $hasBookings }) => {
    if ($today) return "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)";
    if ($weekend) return "#f8fafc";
    return "#ffffff";
  }};
  transition: all 0.22s ease;
  position: relative;
  border-radius: 0;

  &:hover {
    background: ${({ $today, $weekend }) => {
      if ($today) return "linear-gradient(135deg, #e0fce8 0%, #e8fef1 100%)";
      if ($weekend) return "#f1f5f9";
      return "#f0f9ff";
    }};
  }

  ${media.tablet} {
    min-height: 180px;
    padding: 16px;
    gap: 14px;
  }

  ${media.laptop} {
    min-height: 200px;
    padding: 18px;
    gap: 14px;
  }
`;

const CellHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const DayLabel = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 16px;
  font-weight: ${({ $today }) => ($today ? "800" : "700")};
  color: ${({ $today, $weekend }) =>
    $today ? "#059669" : $weekend ? "#64748b" : "#293461"};
  letter-spacing: -0.3px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TodayBadge = styled.span`
  font-size: 9px;
  font-weight: 800;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 2px 8px;
  border-radius: 20px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);
`;

const BookingCountBadge = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1.5px solid #e2e8f0;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  opacity: ${({ $count }) => ($count > 0 ? 1 : 0)};
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }

  ${media.tablet} {
    font-size: 13px;
    padding: 5px 12px;
  }
`;

const BookingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  overflow-y: ${({ $expanded }) => ($expanded ? "visible" : "hidden")};
  max-height: ${({ $expanded }) => ($expanded ? "none" : "calc(100% - 50px)")};
`;

const NoBookingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1;
  padding: 12px 0;
`;

const NoBookingIcon = styled.div`
  font-size: 22px;
  color: #cbd5e1;
  opacity: 0.6;
`;

const NoBookingText = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
  font-style: italic;
`;

const BookingChip = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: linear-gradient(
    135deg,
    ${({ $color }) => `${$color}12`} 0%,
    ${({ $color }) => `${$color}06`} 100%
  );
  border: 1.5px solid ${({ $color }) => `${$color}25`};
  border-left: 4px solid ${({ $color }) => $color};
  border-radius: 10px;
  text-align: left;
  cursor: pointer;
  transition: all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex;
  flex-direction: column;
  gap: 6px;
  background-color: ${({ $color }) => `${$color}08`};
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    border-color: ${({ $color }) => `${$color}40`};
    background: linear-gradient(
      135deg,
      ${({ $color }) => `${$color}15`} 0%,
      ${({ $color }) => `${$color}08`} 100%
    );
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${({ $color }) => $color};
    outline-offset: 2px;
  }

  ${media.tablet} {
    padding: 11px 13px;
  }
`;

const ChipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const ChipTime = styled.span`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 12px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 12px;
  }
`;

const ChipStatus = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 8px;
  background: ${({ $status }) => {
    switch ($status) {
      case "confirmed":
        return "rgba(16, 185, 129, 0.15)";
      case "reserved":
        return "rgba(245, 158, 11, 0.15)";
      default:
        return "rgba(100, 116, 139, 0.15)";
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case "confirmed":
        return "#059669";
      case "reserved":
        return "#d97706";
      default:
        return "#64748b";
    }
  }};
  white-space: nowrap;
`;

const ChipRoom = styled.span`
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ChipRef = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ExpandButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px dashed #e2e8f0;
  border-radius: 10px;
  background: transparent;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.22s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: auto;

  i {
    font-size: 14px;
  }

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
    color: #475569;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid #00838c;
    outline-offset: 2px;
  }

  ${media.tablet} {
    font-size: 12px;
    padding: 9px 11px;
  }
`;

const CollapseButton = styled(ExpandButton)`
  border-style: solid;
  background: #f8fafc;
  color: #475569;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
`;
